/**
 * «Invitar»: el único camino para que alguien empiece a usar su cuenta.
 *
 * Reemplaza a «Generar contraseña temporal» en el perfil del doctor. Eran dos
 * botones para lo mismo —habilitar a una persona— y el de la temporal obligaba
 * al administrador a dictar una clave que el sistema iba a pedir cambiar en el
 * primer ingreso. El enlace lleva directo a elegir contraseña, así que no hay
 * nada que dictar.
 *
 * Lo que se copia es el **mensaje entero**, no el enlace pelado: el canal real
 * es WhatsApp, y el administrador terminaba escribiendo a mano la bienvenida y
 * el «tu usuario es tu correo» en cada invitación.
 */

import { useState } from 'react';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { Button, Field, Modal, Note } from '@/components/ui';

/**
 * El texto que el administrador manda por WhatsApp.
 *
 * «Te damos la bienvenida» y no «Bienvenido»: la mitad del directorio son
 * doctoras, y un mensaje que las trata en masculino se nota.
 */
function inviteMessage(name: string, email: string, url: string): string {
  const firstName = name.trim().split(/\s+/)[0] ?? name;
  return `¡Hola ${firstName}! Te damos la bienvenida a Clean Blue.

Ya podés entrar a tu portal para ver tus cobros y el estado de tu consultorio.

Tu usuario: ${email}
Creá tu contraseña acá: ${url}

El enlace vence en una semana y sirve una sola vez. Después entrás siempre desde cleanblue.cc con tu correo y la contraseña que elijas.`;
}

export function InviteButton({ profileId, personName, email, ghost, label = 'Invitar' }: {
  profileId: string;
  personName: string;
  /** El servidor exige correo: es el nombre de usuario. Sin él no se invita. */
  email: string;
  /** En la galería el botón va sin caja: ver el comentario en `DoctorCard`. */
  ghost?: boolean;
  label?: string;
}) {
  const [invite, setInvite] = useState<{ url: string; emailed: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = useApiMutation(
    () => api.post<{ invite_url: string; emailed: boolean }>(`/admin/profiles/${profileId}/invite`),
    ['/admin/profiles'],
    { onSuccess: (d) => setInvite({ url: d.invite_url, emailed: d.emailed }) },
  );

  function close() {
    setInvite(null);
    setCopied(false);
    generate.reset();
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles el texto queda a la vista para copiarlo a mano.
    }
  }

  const message = invite ? inviteMessage(personName, email, invite.url) : '';

  return (
    <>
      <Button
        variant={ghost ? 'ghost' : undefined}
        disabled={generate.isPending}
        onClick={() => generate.mutate()}
      >
        {generate.isPending ? 'Generando…' : label}
      </Button>

      {generate.error && !invite && (
        <Note tone="danger">{generate.error.message}</Note>
      )}

      {invite && (
        <Modal
          title={`Invitación para ${personName}`}
          onClose={close}
          footer={<Button variant="primary" onClick={close}>Listo</Button>}
        >
          {/* Solo si de verdad salió: el canal real es WhatsApp y puede no
              haber proveedor de correo configurado. */}
          {invite.emailed && (
            <Note tone="ok">También se lo enviamos por correo a {email}.</Note>
          )}

          <p className="small muted mb-2">
            Copiá el mensaje y mandáselo por WhatsApp. El enlace vence en una semana
            y sirve una sola vez.
          </p>

          <textarea
            className="input"
            readOnly
            rows={9}
            value={message}
            onFocus={(e) => e.target.select()}
          />

          <div className="row mt-2">
            <Button variant="primary" onClick={() => void copy(message)}>
              {copied ? 'Copiado ✓' : 'Copiar mensaje'}
            </Button>
          </div>

          {/* El enlace suelto queda a mano por si lo quiere mandar sin el texto. */}
          <Field label="Solo el enlace">
            <input
              className="input mono"
              readOnly
              value={invite.url}
              onFocus={(e) => e.target.select()}
            />
          </Field>
        </Modal>
      )}
    </>
  );
}
